import { LangUtil } from '../../common/utils/lang-util';
import { getState } from '../store';
import { PhraseDetailsCache } from './phrase-details/phraseDetailsService/phrase-details-cache';
import { getActiveGroupTargetLanguage } from '../store/models/selectors';
import { SpeechVoiceUserSelector } from './speechVoiceManager';
import { getAutoSpeechRate } from '../store/general/selectors';

type TOptimizeParagraphsLangs = {
  resultParagraphs: string[],
  resultLangs: string[]
}

export class TextSpeaker {
  id?: string;
  text!: string;
  isParagraph!: boolean;

  private lastStopTime?: Date | null;

  public async speak(_text: string | undefined, lang?: string, stopOnPlay?: boolean, onStop?: () => void, priorityLang?: string,
    id?: string, rate?: number, voice?: SpeechSynthesisVoice
  ): Promise<boolean | undefined> {
    this.id = id;
    const text = this.prepareText(_text);
    const paragraphs = this.getTextParagraphs(text);
    if (paragraphs.length > 1) {
      this.speakParagraphs(paragraphs, lang, stopOnPlay, onStop, priorityLang, rate, voice);
    } else {
      if (lang) {
        return this.speakByLang(text, lang, stopOnPlay, onStop, priorityLang, id, rate, voice);
      }
      const langCode = await TextSpeaker.getLangByText(text);
      return this.speakByLang(text, langCode, stopOnPlay, onStop, priorityLang, id, rate, voice);
    }
  }

  private prepareText(text: string | undefined): string {
    return !!text ? text.replaceAll(/`/g, '') : ''; // todo: why text undefined?
  }

  // No live caller reaches this without a lang (see getLangByText); auto-detection via
  // DetectLangRest was removed as dead weight (LNG-1709) — falls back straight to the active
  // group's target language, same as the removed catch branch did on a failed detect call.
  private async detectParagraphsLangs(paragraphs: string[], priorityLang?: string): Promise<string[]> {
    const lang = getActiveGroupTargetLanguage(getState());
    const langCode = lang?.code || 'en';
    const result = [];
    for(let i=0; i<paragraphs.length; i++)
      result.push(langCode);
    return result;
  }

  private optimizeParagraphsLangs(paragraphs: string[], langs: string[]): TOptimizeParagraphsLangs {
    const resultParagraphs: string[] = [];
    const resultLangs: string[] = [];
    const len = Math.min(paragraphs.length, langs.length);
    let prevLang: string | undefined;
    let currentParagraph = '';
    for (let i=0; i<len; i++) {
      if (langs[i] === prevLang) {
        currentParagraph += ' ' + paragraphs[i];
      } else {
        if (currentParagraph) {
          resultParagraphs.push(currentParagraph.trim());
          resultLangs.push(prevLang!);
        }
        currentParagraph = paragraphs[i];
        prevLang = langs[i];
      }
    }
    if (currentParagraph) {
      resultParagraphs.push(currentParagraph.trim());
      resultLangs.push(prevLang!);
    }
    return {
      resultParagraphs, resultLangs
    }
  }

  private async speakParagraphs(_paragraphs: string[], langCode?: string, stopOnPlay?: boolean, onStop?: () => void, priorityLang?: string,
                                rate?: number, voice?: SpeechSynthesisVoice) {
    if (!speechSynthesis) return false;
    if (speechSynthesis.speaking) {
      speechSynthesis.cancel();
      this.isParagraph = false; // cancel paragraphs
      // if (stopOnPlay) return true; // если 1 играет и кликнуть на 2, то в 1 застрянет playMode
    }
    let index = 0;

    let _langCodes: string[] = [];
    if (!!langCode) {
      _paragraphs.forEach(p => {
        _langCodes.push(langCode)
      })
    } else {
      _langCodes = await this.detectParagraphsLangs(_paragraphs, priorityLang);
    }

    const {resultParagraphs: paragraphs, resultLangs: langCodes} = this.optimizeParagraphsLangs(_paragraphs, _langCodes);

    const onStopCallback = () => {
      if (onStop)
        onStop();
    }

    const _onStop = () => {
      const lastStopInterval = new Date().getTime() - (this.lastStopTime?.getTime() || 0);
      if (lastStopInterval < 100) {
        return onStopCallback();
      }

      index++;
      if (index >= paragraphs.length) {
        return onStopCallback();
      }
      let isNotLastParagraph = index < paragraphs.length - 1;
      playParagraph(isNotLastParagraph);
    }

    const playParagraph = async (isNotLastParagraph?: boolean) => {
      if (!this.isParagraph) return;
      const text = paragraphs[index];
      this.text = text ? TextSpeaker.prepareText(text) : '';
      const _langCode = langCode || (index < langCodes.length ? langCodes[index] : await TextSpeaker.getLangByText(text));
      TextSpeaker.playText({
        id: this.id,
        isNotLastParagraph,
        text: this.text,
        lang: _langCode,
        onStop: _onStop,
        rate,
        voice
      });
    }

    this.isParagraph = true;
    playParagraph(!index && paragraphs.length > 1);
    return true;
  }

  private speakByLang(text: string, lang: string, stopOnPlay?: boolean, onStop?: () => void, priorityLang?: string,
    id?: string, rate?: number, voice?: SpeechSynthesisVoice
  ): boolean {
    if (!speechSynthesis) return false;

    if (speechSynthesis.speaking) {
      speechSynthesis.cancel();
      this.isParagraph = false; // cancel paragraphs
      if (stopOnPlay &&
        text === this.text
      ) return true;
    }

    this.text = TextSpeaker.prepareText(text);
    TextSpeaker.playText({
      text: this.text,
      lang,
      onStop,
      id,
      rate,
      voice
    });
    return true;
  }

  public stop() {
    console.log('TextSpeaker stop')
    if (!speechSynthesis) return false;
    if (speechSynthesis.speaking) {
      speechSynthesis.cancel();
      this.isParagraph = false; // cancel paragraphs

      this.text = '';
      this.lastStopTime = new Date();
    }
  }

  public isPlay(): boolean {
    return speechSynthesis?.speaking;
  }

  public pause() {
    console.log('TextSpeaker pause')
    speechSynthesis.pause();
  }

  public isPaused(): boolean {
    return speechSynthesis.paused;
  }

  // No caller currently omits `lang` (both live call sites — SpeechVoiceSelect and
  // TextPlayerProvider — always pass it), so this is a last-resort fallback rather than a
  // hot path. Used to auto-detect via DetectLangRest (removed as dead weight — LNG-1709);
  // falls back straight to the active group's target language instead.
  private static async getLangByText(text: string): Promise<string> {
    const primaryKey = 'langByText';
    let langCode = PhraseDetailsCache.get([primaryKey, text]);
    if (langCode) {
      return langCode;
    }
    langCode = LangUtil.checkLangCode(getActiveGroupTargetLanguage(getState())?.code);
    PhraseDetailsCache.put([primaryKey, text], langCode);
    return langCode;
  }

  public static prepareText(text: string): string {
    let result = text.replace(/<\/?[^>]+(>|$)/g, ' ');
    result = result.replace(/\d+\./g, ' ');
    result = result.replace(/"|'/g, '');
    result = result.replaceAll('*', '');
    return result;
  }

  private getTextParagraphs(text: string): string[] {
    const separateChars = ['"', '(', ')', ':', '-', '\n'];
    const separateMark = '[separator]'
    let result = text.replaceAll(/<br\/?>/g, '\n')
    separateChars.forEach(sep => {
      result = result.replaceAll(sep, separateMark)
    });
    return result.split(separateMark).map(s => s.trim())
      .filter(s => s.length > 0 && s !== ',' && s!== '.')
  }

  public static playText(params: {
    text: string,
    lang: string,
    onStop?: () => void,
    id?: string,
    isNotLastParagraph?: boolean,
    rate?: number,
    voice?: SpeechSynthesisVoice
  }) {
    if (!params.text) return;
    let isPlaying = false;

    const landCode = LangUtil.getIsoLangCode(params.lang);
    let msg = new SpeechSynthesisUtterance();
    msg.text = params.text;
    msg.lang = landCode;
    msg.rate = params.rate || getAutoSpeechRate(getState());
    const voice = params.voice || SpeechVoiceUserSelector.getVoice(params.lang);
    if (voice) {
      msg.voice = voice;
      console.log('voice: name: ' + voice.name + ', lang: ' + voice.lang);
    } else {
      console.log('voice: default', params.lang);
    }
    msg.onstart = () => {
      window.dispatchEvent(new CustomEvent('speechSynthesisEvent', {
        detail: { message: params.text, type: 'onstart', id: params.id }
      }));
    }
    msg.onerror = (e) => {
      console.error('SpeechSynthesisUtterance error', e);
      isPlaying = false;
      clearInterval(synthesisInterval);
      if (params.onStop) params.onStop();
      window.dispatchEvent(new CustomEvent('speechSynthesisEvent', {
        detail: { message: params.text, type: 'onerror', id: params.id }
      }));
    }
    msg.onend = () => {
      isPlaying = false;
      clearInterval(synthesisInterval);
      if (params.onStop) params.onStop();
      if (!params.isNotLastParagraph) { // sent onend after the last paragraph
        window.dispatchEvent(new CustomEvent('speechSynthesisEvent', {
          detail: { message: params.text, type: 'onend', id: params.id }
        }));
      }
    }

    setTimeout(() => {
      speechSynthesis.cancel(); // без этого не всегда работает speak даже если не играет в данный момент
      speechSynthesis.speak(msg)
    }, 100);
    isPlaying = true;

    // fix chrome bug  https://stackoverflow.com/questions/21947730/chrome-speech-synthesis-with-longer-texts
    const synthesisInterval = setInterval(() => {
      if (isPlaying) {
        speechSynthesis.pause();
        speechSynthesis.resume();
      } else {
        clearInterval(synthesisInterval);
      }
    }, 10000);
  }
}

export const textSpeaker = new TextSpeaker();
